import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Box, Typography, Divider } from "@material-ui/core";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import CustomDatePicker from "components/Input/CustomDatePicker";
import InputAutocompleteAddress from "components/Input/InputAutocompleteAddress";
import AppColor from "utilities/AppColor";
import { useDispatch } from "react-redux";
import { submitIntakeLinkInfoDetails } from "store/slices/client";
import { showToast } from "store/slices/toast";

const IntakeLinkInfoDetails = ({ clientData, token, onNextStep }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [latLng, setLatLng] = useState({});

  // Address validation state
  const [validAddress, setValidAddress] = useState(true);

  // Ref to hold validateForm from Formik render prop
  const validateFormRef = useRef(null);

  // Handle address change (user types without selecting suggestion)
  const handleAddressChange = (value = '') => {
    setValidAddress(value.trim() === clientData?.address1);
  };

  const initialValues = {
    firstName: clientData?.firstName || "",
    lastName: clientData?.lastName || "",
    dateOfBirth: clientData?.dateOfBirth || null,
    address: clientData?.address1 || "",
    postalCode: clientData?.postalCode || "",
    preferredLanguage: clientData?.preferredLanguage || "",
    culturalBackground: clientData?.culturalBackground || "",
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    lastName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    postalCode: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    dateOfBirth: Yup.date().nullable().required(t("validation.required")),
    address: Yup.string()
      .required(t("validation.required"))
      .test(
        'address-suggestion-required',
        'validation.address.must_select_suggestion',
        () => validAddress
      ),
    preferredLanguage: Yup.string().max(255, "validation.max_length_255"),
    culturalBackground: Yup.string().max(255, "validation.max_length_255"),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    const payload = {
      intakeLinkToken: token,
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      address1: values.address,
      postalCode: values.postalCode,
      preferredLanguage: values.preferredLanguage,
      culturalBackground: values.culturalBackground,
      lat: latLng?.lat,
      lng: latLng?.lng,
    };

    try {
      const response = await dispatch(submitIntakeLinkInfoDetails(payload));
      if (response && response.payload) {
        onNextStep(true);
      }
    } catch (e) {
      console.log(e);
      dispatch(showToast({ message: "Failed to submit form", type: "error" }));
    }
    setLoading(false);
  };

  // Reset address validation when clientData changes
  useEffect(() => {
    setValidAddress(true);
  }, [clientData?.address1]);

  // Force Formik re-validation when validAddress changes
  // This is the KEY fix: Yup .test() only runs when Formik validates,
  // so we must manually trigger validation when external state changes
  useEffect(() => {
    if (validateFormRef.current) {
      validateFormRef.current();
    }
  }, [validAddress]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, setFieldValue, values, errors, touched, validateForm }) => {
        // Store validateForm in ref so useEffect can call it
        validateFormRef.current = validateForm;
        return (
        <Form>
          <Typography variant="h6" style={{ color: AppColor.mainColor, marginBottom: 15 }}>
            {t("label.participant_details")}
          </Typography>
          <Divider style={{ marginBottom: 20 }} />
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="firstName" label={t("label.firstName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="lastName" label={t("label.lastName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field
                component={CustomDatePicker}
                label={t("label.dateOfBirth")}
                name="dateOfBirth"
                type="text"
                formatDateJob={true}
                custom={{ disableFuture: true }}
                required
                classInput="date-picker-container-custom"
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="postalCode" label={t("label.postal_code")} required/>
            </div>
            <div className="col-sm-12 col-md-12">
              <Field
                component={InputAutocompleteAddress}
                name="address"
                label={t("label.address")}
                onChangePostalCode={(postalcode) => {
                  setFieldValue("postalCode", postalcode);
                }}
                allowPattern={true}
                onChangeLatLng={(latlng) => {
                  setValidAddress(true);
                  setLatLng(latlng);
                }}
                setValidAddress={setValidAddress}
                onAddressChange={handleAddressChange}
                required
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="preferredLanguage" label={t("label.preferredLanguage")} />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="culturalBackground" label={t("label.culturalBackground")} />
            </div>
          </div>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <AppButton
              label={t("button.saveAndNext")}
              type="submit"
              buttonColor={AppColor.mainColor}
              isLoading={loading}
              disabled={!isValid || loading}
              style={{ width: "150px" }}
            />
          </Box>
        </Form>
        );
      }}
    </Formik>
  );
};

export default IntakeLinkInfoDetails;
